/**
 * Where a proposal's pages end.
 *
 * The document used to be one continuous column on screen and was only cut
 * into pages at export time, inside `./pdf.ts`. That meant the thing an
 * operator read was never the thing the client received: a heading could sit
 * mid-column here and land two lines above a page edge there, and nobody found
 * out until the PDF was open. So the break decisions moved here, in front of
 * the operator — the preview lays the document out on real letter sheets, and
 * the export slices the canvas at the page tops this returned. One set of
 * decisions, two renderings of it.
 *
 * Everything is in CSS pixels, which are 96 to the inch by definition — that
 * is what makes a measurement taken from the DOM convertible to an inch on
 * paper without a fudge factor.
 */
export const PX_PER_IN = 96;
export const PAGE_MARGIN_IN = 0.5;
export const PAGE_WIDTH_PX = 8.5 * PX_PER_IN;
export const PAGE_HEIGHT_PX = 11 * PX_PER_IN;
export const PAGE_MARGIN_PX = PAGE_MARGIN_IN * PX_PER_IN;
/** 7.5in — the document's column, and the width the PDF scales the canvas to. */
export const CONTENT_WIDTH_PX = PAGE_WIDTH_PX - PAGE_MARGIN_PX * 2;
/** 10in — how much of the document fits on one sheet. */
export const CONTENT_HEIGHT_PX = PAGE_HEIGHT_PX - PAGE_MARGIN_PX * 2;
/**
 * The gutter between two sheets. It is real height in the flow rather than a
 * visual trick, which is why the export slices at the page tops recorded below
 * instead of at multiples of a page — the gutters are simply not part of any
 * page.
 */
export const SHEET_GAP_PX = 28;

/** A run of the document that should not be cut in half if it can be helped. */
type Block = {
  el: HTMLElement;
  /**
   * Set when a block stands for less than its own box: a prose section too tall
   * to fit anywhere is broken at its paragraphs, and the section element then
   * accounts only for the heading above them.
   */
  height?: number;
};

/**
 * The document's breakable units, in order.
 *
 * `[data-block]` marks them, and none of those elements may carry a React
 * `style` prop — the shift below is written to `style.marginTop`, and a
 * re-render that rewrote the style attribute would silently drop it.
 */
function collectBlocks(root: HTMLElement): Block[] {
  const out: Block[] = [];
  for (const block of Array.from(root.querySelectorAll<HTMLElement>("[data-block]"))) {
    const prose = block.querySelector<HTMLElement>(".ProseMirror");
    const box = block.getBoundingClientRect();
    // A scope of work runs longer than a page routinely, and no amount of
    // break avoidance will make it fit. Descending to its paragraphs turns an
    // arbitrary cut through a line of type into a cut between two paragraphs.
    if (prose && box.height > CONTENT_HEIGHT_PX && prose.children.length > 0) {
      // The section stands for its heading *and* the paragraph under it, so a
      // break can never leave a title alone at the foot of a page. Capped at a
      // page: a lead paragraph longer than that fits nowhere, and the cap turns
      // the block into one that starts a fresh page rather than one that
      // triggers the run-on arm below.
      const first = prose.children[0] as HTMLElement;
      const lead = first.getBoundingClientRect().bottom - box.top;
      out.push({ el: block, height: Math.min(lead, CONTENT_HEIGHT_PX) });
      for (const child of Array.from(prose.children)) out.push({ el: child as HTMLElement });
      continue;
    }
    out.push({ el: block });
  }
  return out;
}

function clearShifts(root: HTMLElement) {
  for (const el of Array.from(root.querySelectorAll<HTMLElement>("[data-page-shift]"))) {
    el.style.marginTop = "";
    el.removeAttribute("data-page-shift");
  }
}

/**
 * Pushes `el` down until its top sits at `target`, and reports what that
 * actually bought.
 *
 * Measured rather than assumed, because a margin written onto a paragraph
 * collapses with the margin above it: asking for 300px of whitespace and
 * getting 280 would leave the previous page's last line repeated at the top of
 * the next one. The second correction lands it — the residual is linear.
 */
function pushDown(el: HTMLElement, target: number, rootTop: number): number {
  const before = el.getBoundingClientRect().top - rootTop;
  for (let pass = 0; pass < 2; pass++) {
    const own = Number.parseFloat(getComputedStyle(el).marginTop) || 0;
    const at = el.getBoundingClientRect().top - rootTop;
    if (pass > 0 && Math.abs(at - target) < 0.5) break;
    el.style.marginTop = `${own + (target - at)}px`;
    el.setAttribute("data-page-shift", "");
  }
  return el.getBoundingClientRect().top - rootTop - before;
}

/**
 * Lays `root` out on letter sheets and returns the top of each page, in the
 * document's own coordinates (0 is the first line of the first page).
 *
 * Idempotent: it clears the whitespace it added last time before measuring, so
 * running it again on the same DOM — which a resize, a font arriving, or an
 * edit all do — yields the same answer rather than compounding.
 */
export function paginate(root: HTMLElement): number[] {
  clearShifts(root);

  const rootTop = root.getBoundingClientRect().top;
  const blocks = collectBlocks(root);

  const pageTops = [0];
  // Natural coordinates ignore the whitespace this pass has inserted, so a
  // block's position means the same thing before and after a break above it.
  let pageStart = 0;
  let shift = 0;

  for (const b of blocks) {
    const rect = b.el.getBoundingClientRect();
    const top = rect.top - rootTop - shift;
    const height = b.height ?? rect.height;
    const bottom = top + height;
    if (bottom - pageStart <= CONTENT_HEIGHT_PX) continue;

    if (height <= CONTENT_HEIGHT_PX && top > pageStart) {
      const target = pageTops[pageTops.length - 1]! + CONTENT_HEIGHT_PX + SHEET_GAP_PX;
      shift += pushDown(b.el, target, rootTop);
      pageStart = top;
      pageTops.push(top + shift);
      continue;
    }

    // Taller than a page even on its own: nothing can keep it whole, so it
    // runs across as many sheets as it needs and the next block starts from
    // wherever that left the cursor.
    let start = pageStart;
    while (bottom - start > CONTENT_HEIGHT_PX) {
      start += CONTENT_HEIGHT_PX;
      pageTops.push(start + shift);
    }
    pageStart = start;
  }

  return pageTops;
}

export function samePages(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((v, i) => Math.abs(v - b[i]!) < 0.5);
}
